import * as amplitude from "@amplitude/analytics-browser";
import { ManagedUserRoles } from "@redotech/account-common/roles";
import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { useContext, useEffect } from "react";
import { PlatformEvents } from "../../../platform-events";
import { ActionRequiredSeverity } from "./action-required-notification";

/**
 * Banner shown to merchant-team admins when Redo has enabled Personalized
 * Inserts monetization but the merchant has not yet accepted the payment terms.
 * The banner's action navigates to the Personalized Inserts page, where the
 * terms are embedded for review-and-accept; acceptance unlocks inserts (the
 * page/handlers gate on monetization enabled + terms accepted).
 */
export function usePersonalizedInsertsTerms(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const user = useContext(UserContext);
  const team = useContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isAdmin =
    user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN) ?? false;
  const monetization =
    team?.settings?.outboundLabels?.personalizedSlipsMonetization;
  const termsAcceptedAt =
    team?.settings?.outboundLabels?.personalizedSlipsTermsAcceptedAt;

  const isVisible =
    isAdmin && !!monetization?.enabled && termsAcceptedAt == null;

  useEffect(() => {
    if (isVisible) {
      amplitude.track(PlatformEvents.PERSONALIZED_INSERTS_TERMS_VIEWED);
    }
  }, [isVisible]);

  if (!isAdmin || !monetization?.enabled || termsAcceptedAt != null) {
    return null;
  }

  const item: NotificationBannerItem = {
    id: "personalized-inserts-terms",
    type: "info",
    message:
      "Review and accept the Personalized Inserts billing terms to start using inserts.",
    actions: [
      {
        actionType: "link",
        internal: true,
        text: "Review terms",
        href: routes.getOrdersOutboundLabelsSubRoute("personalized-inserts"),
        onClick: () =>
          amplitude.track(
            PlatformEvents.PERSONALIZED_INSERTS_TERMS_REVIEW_CLICKED,
          ),
      },
    ],
  };

  return { severity: ActionRequiredSeverity.Medium, item };
}
